import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { EnumOption } from "@/lib/enums";

// Radix Select forbids an empty-string item value, so filters that want a
// "no filter" choice need a sentinel. We map "" (caller-facing) <-> ALL_VALUE
// (Radix-facing) internally when `allOption` is provided.
const ALL_VALUE = "__all__";

interface EnumSelectProps {
  /** Current value. Pass "" to represent "nothing selected" — only valid with `allOption`. */
  value: string;
  onValueChange: (value: string) => void;
  options: readonly EnumOption[];
  /** Trigger placeholder shown when value is empty. */
  placeholder?: string;
  /**
   * When set, prepends an "all" item that resolves to "". Use this for toolbar
   * filters. Omit it for required form fields where a value is always selected.
   */
  allOption?: { label: string };
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
}

/**
 * A typed wrapper over the shadcn `Select` for choosing from a fixed enum.
 *
 * - Form field:   `<EnumSelect value={field.value} onValueChange={field.onChange} options={PRODUCT_STATUS_OPTIONS} />`
 * - Toolbar filter: `<EnumSelect value={status} onValueChange={setStatus} options={...} allOption={{ label: "All statuses" }} />`
 */
export function EnumSelect({
  value,
  onValueChange,
  options,
  placeholder,
  allOption,
  className,
  disabled,
  "aria-label": ariaLabel,
}: EnumSelectProps) {
  const selectValue = allOption && value === "" ? ALL_VALUE : value;

  function handleChange(next: string) {
    onValueChange(next === ALL_VALUE ? "" : next);
  }

  return (
    <Select value={selectValue} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger className={className} aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allOption && <SelectItem value={ALL_VALUE}>{allOption.label}</SelectItem>}
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
